import React from "react";
import {
    components,
    ContainerProps,
    ControlProps,
    DropdownIndicatorProps,
    InputProps,
    NoticeProps,
    OptionProps,
    PlaceholderProps,
    SingleValueProps,
    ValueContainerProps,
    ClearIndicatorProps,
} from "react-select";
import AngleLeftGray from "../../assets/icons/sign/AngleLeftGray.svg";
import COLORS from "../../constants/colors";
import Typography from "../Typography";
import { SelectOption } from "./Select.types";

export const ContainerComponent = ({
    children,
    ...props
}: ContainerProps<SelectOption>) => {
    return (
        <components.SelectContainer {...props} className="select-container">
            {children}
        </components.SelectContainer>
    );
};

export const ControlComponent = ({
    children,
    className,
    inlineLabel,
    ...props
}: ControlProps<SelectOption> & { inlineLabel?: string }) => {
    const { selectProps } = props;
    const customLabel = inlineLabel ?? selectProps["aria-label"] ?? "";
    return (
        <components.Control
            {...props}
            className={[
                className,
                "select-control",
                selectProps.isSearchable ? "select-control--searchable" : null,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {customLabel ? (
                <Typography
                    data-select-inline-label
                    weight="medium"
                    color={COLORS.GREY_600}
                >
                    {customLabel}
                </Typography>
            ) : null}
            {children}
        </components.Control>
    );
};

export const DropdownIndicatorComponentWrapper = ({
    children,
    ...props
}: DropdownIndicatorProps<SelectOption>) => {
    return (
        <components.DropdownIndicator
            {...props}
            className="select-customDropDownIndicator"
        >
            {children}
        </components.DropdownIndicator>
    );
};
export const DropdownIndicatorComponent = ({
    children,
    ...props
}: DropdownIndicatorProps<SelectOption>) => {
    return (
        <components.DropdownIndicator
            {...props}
            className="select-dropDownIndicator"
        >
            <AngleLeftGray />
        </components.DropdownIndicator>
    );
};

export const SingleValueComponent = ({
    children,
    ...props
}: SingleValueProps<SelectOption>) => {
    const {
        data: { leftNode, rightNode },
    } = props;
    return (
        <components.SingleValue {...props} className="select-singleValue">
            {leftNode}
            <Typography data-select-value-label size="m">
                {children}
            </Typography>
            {rightNode}
        </components.SingleValue>
    );
};

export const InputComponent = (props: InputProps<SelectOption>) => {
    return <components.Input {...props} className="select-input" />;
};

export const PlaceholderComponent = (props: PlaceholderProps<SelectOption>) => {
    return <components.Placeholder {...props} className="select-placeholder" />;
};

export const ValueComponent = (props: ValueContainerProps<SelectOption>) => {
    return (
        <components.ValueContainer
            {...props}
            className="select-value-container"
        />
    );
};

export const OptionComponent = (props: OptionProps<SelectOption>) => {
    const {
        isDisabled,
        isSelected,
        isFocused,
        children,
        data: { leftNode, rightNode },
    } = props;

    let state = "";
    if (isSelected) {
        state = "selected";
    } else if (isDisabled) {
        state = "disabled";
    } else if (isFocused) {
        state = "focused";
    }
    return (
        <components.Option {...props} className={`select-option ${state}`}>
            {leftNode} {children} {rightNode}
        </components.Option>
    );
};

export const NoOptionsMessageComponent = (props: NoticeProps<SelectOption>) => {
    return (
        <components.NoOptionsMessage
            {...props}
            className="select-noOptionMessage"
        />
    );
};

export const ClearIconComponent = (
    props: ClearIndicatorProps<SelectOption>,
) => {
    const {
        innerProps,
        selectProps: { instanceId },
    } = props;
    return (
        <components.ClearIndicator
            {...props}
            className="select-clear"
            innerProps={{
                ...innerProps,
                id: `${instanceId}__clearBtn`,
            }}
        />
    );
};
